import {
  Purchases,
  PackageType,
  ReservedCustomerAttribute,
  ErrorCode,
  type CustomerInfo,
  type EntitlementInfo,
  type Offering,
  type Offerings as RcOfferings,
  type Package as RcPackage,
} from '@revenuecat/purchases-js';
import type {
  IPurchaseService,
  PurchaseAttributeKey,
  PurchaseCustomerInfo,
  PurchaseEntitlement,
  PurchaseErrorCode,
  PurchaseOffering,
  PurchaseOfferings,
  PurchasePackage,
  PurchasePackageType,
  PurchasePeriodType,
} from '@breath-lab/services';

function mapPackageType(t: PackageType): PurchasePackageType {
  switch (t) {
    case PackageType.Monthly:
      return 'monthly';
    case PackageType.Annual:
      return 'annual';
    case PackageType.Lifetime:
      return 'lifetime';
    default:
      return 'unknown';
  }
}

function mapPackage(p: RcPackage): PurchasePackage {
  const product = p.webBillingProduct;
  return {
    identifier: p.identifier,
    packageType: mapPackageType(p.packageType),
    productIdentifier: product.identifier,
    price: {
      amountMicros: product.currentPrice.amountMicros,
      currency: product.currentPrice.currency,
      formattedPrice: product.currentPrice.formattedPrice,
    },
  };
}

function mapOffering(o: Offering): PurchaseOffering {
  return {
    identifier: o.identifier,
    availablePackages: o.availablePackages.map(mapPackage),
  };
}

function mapOfferings(o: RcOfferings): PurchaseOfferings {
  const all: Record<string, PurchaseOffering> = {};
  for (const [id, offering] of Object.entries(o.all)) {
    all[id] = mapOffering(offering);
  }
  return {
    current: o.current ? mapOffering(o.current) : null,
    all,
  };
}

// RC web SDK PeriodType is 'normal' | 'intro' | 'trial' | 'prepaid'. Pass
// 'trial'/'intro' through; collapse 'prepaid' (and any future value) to
// 'normal'. Without this, trial purchases looked like normal ones on web and
// the upgrade flow never fired trial_started.
function mapPeriodType(p: EntitlementInfo['periodType']): PurchasePeriodType {
  return p === 'trial' || p === 'intro' ? p : 'normal';
}

function mapEntitlement(e: EntitlementInfo): PurchaseEntitlement {
  return {
    identifier: e.identifier,
    isActive: e.isActive,
    willRenew: e.willRenew,
    productIdentifier: e.productIdentifier,
    expirationDate: e.expirationDate,
    periodType: mapPeriodType(e.periodType),
  };
}

function mapCustomerInfo(c: CustomerInfo): PurchaseCustomerInfo {
  const active: Record<string, PurchaseEntitlement> = {};
  const all: Record<string, PurchaseEntitlement> = {};
  for (const [id, ent] of Object.entries(c.entitlements.active)) {
    active[id] = mapEntitlement(ent);
  }
  for (const [id, ent] of Object.entries(c.entitlements.all)) {
    all[id] = mapEntitlement(ent);
  }
  return {
    originalAppUserId: c.originalAppUserId,
    entitlements: { active, all },
    managementURL: c.managementURL,
  };
}

const ATTRIBUTE_MAP: Record<PurchaseAttributeKey, ReservedCustomerAttribute> = {
  email: ReservedCustomerAttribute.Email,
  displayName: ReservedCustomerAttribute.DisplayName,
  fcmToken: ReservedCustomerAttribute.FCMTokens,
};

export class WebPurchaseService implements IPurchaseService {
  /** Web Billing offering — packages are `bl.web.*` products, distinct from the store offering. */
  readonly primaryOfferingId = 'web-offering-v3';
  /** Caches the last-fetched offerings so `purchasePackage(DTO)` can locate the underlying RC Package. */
  private lastOfferings: RcOfferings | null = null;
  /** Web SDK requires appUserId at configure time, so we stash the key and defer real configure to logIn. */
  private pendingApiKey: string | null = null;

  isConfigured(): boolean {
    return Purchases.isConfigured();
  }

  configure(apiKey: string): void {
    this.pendingApiKey = apiKey;
  }

  async logIn(uid: string): Promise<PurchaseCustomerInfo> {
    // Offerings fetched under a previous identity must not feed a purchase
    // for this one — purchasePackage resolves RC Package objects from this
    // cache.
    this.lastOfferings = null;
    if (!Purchases.isConfigured()) {
      if (!this.pendingApiKey) {
        throw new Error('[WebPurchaseService] logIn called before configure');
      }
      Purchases.configure({ apiKey: this.pendingApiKey, appUserId: uid });
    } else {
      await Purchases.getSharedInstance().changeUser(uid);
    }
    const info = await Purchases.getSharedInstance().getCustomerInfo();
    return mapCustomerInfo(info);
  }

  async logOut(): Promise<PurchaseCustomerInfo> {
    // Web has no off-device transactions. Identity stays bound to the current
    // appUserId until the next logIn switches it. Return current state if RC
    // is up; otherwise return an empty stub so callers can fire-and-forget.
    if (!Purchases.isConfigured()) {
      return {
        originalAppUserId: '',
        entitlements: { active: {}, all: {} },
        managementURL: null,
      };
    }
    return this.getCustomerInfo();
  }

  async setAttributes(attrs: Partial<Record<PurchaseAttributeKey, string | null>>): Promise<void> {
    if (!this.isConfigured()) return;
    const mapped: Record<string, string | null> = {};
    for (const [k, v] of Object.entries(attrs)) {
      const rcKey = ATTRIBUTE_MAP[k as PurchaseAttributeKey];
      if (rcKey) mapped[rcKey] = v;
    }
    if (Object.keys(mapped).length === 0) return;
    await Purchases.getSharedInstance().setAttributes(mapped);
  }

  async getCustomerInfo(): Promise<PurchaseCustomerInfo> {
    const info = await Purchases.getSharedInstance().getCustomerInfo();
    return mapCustomerInfo(info);
  }

  async getOfferings(): Promise<PurchaseOfferings> {
    const offerings = await Purchases.getSharedInstance().getOfferings();
    this.lastOfferings = offerings;
    return mapOfferings(offerings);
  }

  async restorePurchases(): Promise<PurchaseCustomerInfo> {
    // Web has no off-device transactions to restore. The App User ID is the
    // link — re-fetching customer info is the equivalent of a native restore.
    return this.getCustomerInfo();
  }

  async purchasePackage(pkg: PurchasePackage): Promise<PurchaseCustomerInfo> {
    let rcPkg = this.findRcPackage(pkg.identifier);
    if (!rcPkg) {
      // The purchase gate calls logIn() right before purchasing, which drops
      // lastOfferings (offerings from a previous identity must never feed a
      // purchase). Refetch under the now-bound identity instead of failing —
      // same self-sufficiency as NativePurchaseService.purchasePackage.
      await this.getOfferings();
      rcPkg = this.findRcPackage(pkg.identifier);
    }
    if (!rcPkg) {
      throw new Error(
        `[purchases] cannot locate package "${pkg.identifier}" in current offerings`,
      );
    }
    const result = await Purchases.getSharedInstance().purchasePackage(rcPkg);
    return mapCustomerInfo(result.customerInfo);
  }

  errorCodeOf(err: unknown): PurchaseErrorCode | null {
    if (err == null || typeof err !== 'object') return null;
    const code = (err as { errorCode?: number }).errorCode;
    if (code === ErrorCode.UserCancelledError) return 'user_cancelled';
    if (typeof code === 'number') return 'unknown';
    return null;
  }

  private findRcPackage(identifier: string): RcPackage | null {
    if (!this.lastOfferings) return null;
    for (const offering of Object.values(this.lastOfferings.all)) {
      const match = offering.availablePackages.find((p) => p.identifier === identifier);
      if (match) return match;
    }
    return null;
  }
}
